'use client';

import { useCallback } from 'react';

import { Button } from '@naumu/ui/components/button';
import { haptic } from '@naumu/ui/lib/haptics/haptics';

import { BOOK_DEMO_HREF, BOOK_DEMO_LABEL, BOOK_DEMO_LINK_PROPS } from '../config/book-demo';
import {
	CTA_REASSURANCE,
	CTA_SECONDARY,
	HEADER_TRY_HREF,
} from '../constants';
import { useGuestProvisioning } from '@naumu/platform-client/use-guest-provisioning';
import { resolveCrossAppHref } from '@naumu/shared/routes/origins';

/**
 * ProductStageCta - the page-specific closing beat of a stage page: the page's
 * own CTA heading (Linear's "Experience the diff" move) over the Get started /
 * Book a demo pair. Get started is a real link (progressive enhancement, the
 * SAME pattern as the landing hero and header): a plain click provisions a
 * guest space instantly (the button holds its shape and spins), while modified clicks
 * (cmd/middle/shift) and no-JS fall through to the href; Book a demo is the
 * quiet outlined secondary.
 */
export const ProductStageCta = ({ heading }: { heading: string }) => {
	const { provisionAndGo, submitting } = useGuestProvisioning();

	// The in-flight state lives on the DS Button's `loading` prop: same size,
	// label hidden at opacity 0, spinner centred over it. `loading` also keeps
	// pointer-events on the disabled button so the click can't fall through to
	// the wrapping <a> and navigate away mid-provision.
	const handleStart = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			// Modified clicks keep the browser's own behaviour (new tab etc.).
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
				return;
			}
			event.preventDefault();
			if (submitting) return;
			haptic('medium');
			provisionAndGo();
		},
		[provisionAndGo, submitting],
	);

	return (
		<section className="mk-container mk-pad py-[clamp(64px,10vw,160px)]">
			<div className="flex flex-col items-center text-center">
				<h2 className="mk-type-cta text-balance text-neutral">
					{heading}
				</h2>
				<div className="mt-8 flex flex-wrap items-center justify-center gap-3">
					{/* /start is a PLATFORM route - resolve it so the no-JS/modified-click
					    fallback lands on the right origin in local dev. */}
					<a href={resolveCrossAppHref(HEADER_TRY_HREF, 'marketing')} onClick={handleStart}>
						<Button variant="solid-strong" size="md" type="button" loading={submitting}>
							{CTA_SECONDARY}
						</Button>
					</a>
					<a href={BOOK_DEMO_HREF} {...BOOK_DEMO_LINK_PROPS}>
						<Button variant="outline" size="md" type="button">
							{BOOK_DEMO_LABEL}
						</Button>
					</a>
				</div>
				<p className="mt-4 text-[13px] text-neutral/40">{CTA_REASSURANCE}</p>
			</div>
		</section>
	);
};
